import type React from "react";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { FileText } from "lucide-react";
import { looksLikeId } from "@/lib/regex";
import { cn } from "@/lib/utils";
import { UploadItem } from "./types/upload-item";

interface UploadFeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  uploads: UploadItem[];
}

const UploadFeedbackDialog: React.FC<UploadFeedbackDialogProps> = ({
  isOpen,
  onClose,
  uploads,
}) => {
  const uploadsWithFeedback = uploads.filter(
    (upload) => upload.feedback && Object.keys(upload.feedback).length > 0
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Upload feedback</DialogTitle>
          <DialogDescription>
            Feedback fra backend-behandling af dine uploadede filer.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="overflow-x-hidden">
          <Accordion type="single" collapsible className="w-full">
            {uploadsWithFeedback.map((upload, index) => (
              <AccordionItem value={`item-${index}`} key={upload.id}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <FileText className="size-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm font-medium">{upload.name}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {upload.feedback ? (
                    <ul className="ml-2 space-y-2 pt-1 pb-2">
                      {Object.entries(upload.feedback).map(([key, message]) => {
                        const label = key.replace(/:$/, "");
                        return (
                          <li
                            key={key}
                            className="border-l-2 border-muted pl-3 space-y-0.5"
                          >
                            <p className="text-xs font-medium text-foreground capitalize">
                              {label}
                            </p>
                            <p
                              className={cn(
                                "text-xs text-muted-foreground break-all",
                                looksLikeId(message) && "font-mono"
                              )}
                            >
                              {message.length > 0 ? message : "Ingen feedback"}
                            </p>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-xs text-muted-foreground pl-3 py-2">
                      Ingen detaljeret feedback.
                    </p>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>Luk</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadFeedbackDialog;
